import React, { useState, useEffect } from 'react';
import api from '../../../utils/api';
import BookingCard from './BookingCard';
import ReceiptModal from './ReceiptModal';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ReceiptReviewQueue = ({ salonId }) => {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // States for Receipt Modal
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);

  const fetchReceipts = async () => {
    try {
      setLoading(true);
      setError(null);
      // فراخوانی روت رزروها با وضعیت بررسی رسید (review)
      const response = await api.get(`/salons/${salonId}/bookings?status=review`);
      setBookings(response.data);
    } catch (err) {
      console.error('Error fetching receipts:', err);
      setError('خطا در دریافت لیست رسیدهای پرداخت. لطفاً دوباره تلاش کنید.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (salonId) {
      fetchReceipts();
    }
  }, [salonId]);

  const handleApprove = async (bookingId) => {
    try {
      // به صورت آپتیمیستیک رزرو را از لیست حذف می‌کنیم
      setBookings(prev => prev.filter(b => b._id !== bookingId));

      // فراخوانی متد آپدیت استاتوس برای تغییر وضعیت به تایید شده
      await api.patch(`/salons/${salonId}/bookings/${bookingId}/updatestatus`, {
        status: 'confirmed'
      });
    } catch (err) {
      console.error('Error confirming booking:', err);
      // در صورت بروز خطا، کل لیست را مجدداً دریافت می‌کنیم
      fetchReceipts();
      alert('خطا در تایید رزرو رخ داد.');
    }
  };

  const handleReject = async (bookingId) => {
    try {
      // به صورت آپتیمیستیک رزرو را از لیست حذف می‌کنیم
      setBookings(prev => prev.filter(b => b._id !== bookingId));

      // فراخوانی متد آپدیت استاتوس برای تغییر وضعیت به لغو شده
      await api.patch(`/salons/${salonId}/bookings/${bookingId}/updatestatus`, {
        status: 'cancelled'
      });
    } catch (err) {
      console.error('Error rejecting booking:', err);
      // در صورت بروز خطا، لیست را دوباره می‌گیریم
      fetchReceipts();
      alert('خطا در لغو رزرو رخ داد.');
    }
  };

  const openReceiptModal = (booking) => {
    setSelectedBooking(booking);
    setModalOpen(true);
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-12 gap-3">
        <Icon name="Loader2" size={32} className="animate-spin text-primary" />
        <span className="text-muted-foreground text-sm">در حال بارگذاری رسیدها...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-error/10 border border-error/20 p-4 rounded-xl text-error flex flex-col items-center gap-3 text-center" dir="rtl">
        <Icon name="AlertTriangle" size={24} />
        <span>{error}</span>
        <Button variant="outline" size="sm" onClick={fetchReceipts}>تلاش مجدد</Button>
      </div>
    );
  }

  return (
    <div className="space-y-4" dir="rtl">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-bold text-foreground flex items-center gap-2">
          <span>رسیدهای نیازمند بررسی</span>
          <span className="bg-primary/10 text-primary text-xs px-2 py-0.5 rounded-full border border-primary/20">
            {bookings.length} رسید
          </span>
        </h2>
        <Button variant="ghost" size="sm" iconName="RefreshCw" onClick={fetchReceipts}>
          بارگذاری مجدد
        </Button>
      </div>

      {bookings.length === 0 ? (
        <div className="bg-card border border-border rounded-xl p-8 text-center flex flex-col items-center justify-center gap-2 text-muted-foreground">
          <Icon name="CheckCircle2" size={40} className="text-success" />
          <span className="font-medium text-foreground">همه چیز مرتب است!</span>
          <span className="text-sm">هیچ رسیدی در انتظار بررسی وجود ندارد.</span>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {bookings.map((booking) => (
            <BookingCard
              key={booking._id}
              booking={booking}
              onApprove={handleApprove}
              onReject={handleReject}
              onViewReceipt={openReceiptModal}
              showActions={true}
            />
          ))}
        </div>
      )}

      {/* Receipt Modal for full image check */}
      <ReceiptModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        receiptUrl={selectedBooking?.receiptUrl}
        booking={selectedBooking}
        onApprove={handleApprove}
        onReject={handleReject}
      />
    </div>
  );
};

export default ReceiptReviewQueue;
